import { useRef } from 'react';
import {
  useIsFetching,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query';

/**
 * Generic "pull this list again" behaviour, shared by every manual refresh button (Issued / Received,
 * Summary, Drafts, Outgoing). Lifted out of `useRefreshInvoices` so the three list families can reuse it
 * rather than each growing their own copy.
 *
 * Before refetching, each loaded list under [queryKey] is collapsed back to its first page: an infinite
 * query would otherwise re-fetch *every* page the user had scrolled through, so a refresh becomes "back
 * to the top, page 1". Non-infinite queries under the same prefix (summary, detail) have no `pages` and
 * pass through untouched.
 *
 * `isFetching` tracks any in-flight request under [queryKey] so the trigger can show progress — which is
 * why callers should pass the *narrowest* key that covers the list (see the `'list'`-scoping note on
 * `useRefreshDrafts`), not a broad prefix that also carries unrelated traffic.
 *
 * @param opts.also extra keys to invalidate alongside the list (sync schedule, badge counts, …).
 */
export function useRefreshList(queryKey: QueryKey, opts: { also?: QueryKey[] } = {}) {
  const queryClient = useQueryClient();
  const isFetching = useIsFetching({ queryKey }) > 0;
  const also = opts.also;
  const refresh = () => {
    queryClient.setQueriesData<InfiniteData<unknown>>({ queryKey }, (data) =>
      data && Array.isArray(data.pages)
        ? { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) }
        : data,
    );
    for (const key of also ?? []) void queryClient.invalidateQueries({ queryKey: key });
    return queryClient.invalidateQueries({ queryKey });
  };
  return { refresh, isFetching };
}

/**
 * Sends a list page's scroll position back to the top, to match a refresh that collapsed the list to
 * page 1. Spread `rootRef` onto the page's outermost element: desktop scrolls inside the table's
 * ScrollArea viewport (found within that subtree), mobile scrolls the window.
 */
export function useScrollToTop() {
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollToTop = () => {
    rootRef.current
      ?.querySelector<HTMLElement>('[data-slot="scroll-area-viewport"]')
      ?.scrollTo?.({ top: 0 });
    window.scrollTo?.({ top: 0 });
  };
  return { rootRef, scrollToTop };
}
